import { Component, EventEmitter } from '@angular/core';
import { first } from 'rxjs/operators';
import { AircraftSearchCriteria } from '../../../aircraft/components/aircraft-search/aircraft-search-criteria.model';
import { AircraftSearchBarService } from './aircraft-search-bar.service';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { AddAircraftModalComponent, AircraftAddModalData } from '../../../aircraft/components/shared/add-aircraft-modal/add-aircraft-modal.component';
import { MvdModalService } from '../../services/modal/mvd-modal.service';

@Component({
    selector: 'aircraft-search-bar',
    inputs: ['businessTransactionTypeId', 'customer', 'aircraftId', 'readOnly'],
    outputs: ['onItemRemoved', 'onItemSelected'],
    templateUrl: './aircraft-search-bar.html',
})

export class AircraftSearchBarComponent extends BaseComponent {
    /**
     * Results from the search
     */
    public results: any[] = [];

    /**
     * Determines whether or not the search has been run
     */
    public hasSearched = false;

    /**
     * FAA number used for the search
     */
    public faaNumberSearch: string;

    /**
     * The found aircraft record
     */
    public aircraft: any;

    /**
     * Aircraft ID passed in as an input parameter
     */
    public aircraftId: number;

    /**
     * Event for the found aircraft being removed
     */
    public onItemRemoved = new EventEmitter<any>();

    /**
     * Event for the aircraft being found in search
     */
    public onItemSelected = new EventEmitter<any>();

    /**
     * Read only variable for the form
     */
    public readOnly = false;

    constructor(private aircraftSearchService: AircraftSearchBarService,
        private modalService: MvdModalService) {
        super();
    }

    /**
     * Initializes the component
     */
    public async ngOnInit() {
        this.faaNumberSearch = '';
        if (this.aircraftId) {
            let result = await this.aircraftSearchService.getAircraftInformation(this.aircraftId).pipe(first()).toPromise();
            this.aircraft = result;
            this.hasSearched = true;
        }
    }

    /**
     * Searches for the entered FAA number
     */
    public search = async (): Promise<void> => {
        if (this.validateSearchCriteria()) {
            await this.retrieveSearchResults();
        } else {
            this.results = [];
        }
    };

    /**
     * Event handler for the FAA search change
     */
    public faaNumberSearchChange() {
        this.hasSearched = false;
    }

    /**
     * Event handler for the remove aircraft
     */
    public removeAircraft() {
        this.aircraft = null;
        this.hasSearched = false;
        this.onItemRemoved.emit();
    }

    /**
     * Event handler for the add aircraft
     */
    public async addAircraft() {
        const data = new AircraftAddModalData(this.faaNumberSearch);
        let aircraftId = await this.modalService.open(AddAircraftModalComponent, data).afterClosed().pipe(first()).toPromise();
        if (aircraftId) {
            this.aircraftId = aircraftId;
            let result = await this.aircraftSearchService.getAircraftInformation(aircraftId).pipe(first()).toPromise();
            this.aircraft = result;
            this.aircraft.aircraftId = this.aircraft.id;
            this.hasSearched = true;
            this.onItemSelected.emit(this.aircraft);
        }
    }

    /**
     * Performs the search
     */
    private retrieveSearchResults = async (): Promise<void> => {
        let aircraftSearchCriteria = new AircraftSearchCriteria();

        if (this.faaNumberSearch.trim().length > 0) {
            aircraftSearchCriteria.faaNumber = this.faaNumberSearch.trim();
        }

        let resultsFound = await this.aircraftSearchService.search(aircraftSearchCriteria).pipe(first()).toPromise();

        this.hasSearched = true;

        if (resultsFound && resultsFound.results.length > 0) {
            this.aircraft = resultsFound.results[0];
            this.onItemSelected.emit(this.aircraft);
        } else {
            this.aircraft = null;
        }
    };

    /**
     * Validates the entered search criteria
     */
    private validateSearchCriteria = (): boolean => {
        let isValidCriteria = false;
        if (this.faaNumberSearch.trim().length > 0) {
            isValidCriteria = true;
        }

        return isValidCriteria;
    };
}
